// This class originally appeared in VizPortal
import * as React from 'react';
import * as shallowEqual from 'shallowequal';

import * as SparklineStyles from './SparklineStyles';
import { Normalizer, OffsetCorrector, PathBuilder } from './SparklineUtilities';

export interface SparklineProps {

  // At least two data points are required for the sparkline to render correctly
  readonly dataPoints: ReadonlyArray<number>;

  // The pixel dimensions of the sparkline element, including any required padding.
  // Since SVG paths only support absolute coordinates, we need to know the size up front.
  readonly width: number;
  readonly height: number;
  readonly title: string;
}

export interface SparklineState {
  readyForFullRender: boolean;
  labeledDataPoints: DataPointLabelSpec[];
}

export interface DataPointLabelSpec {
  key: string;
  index: number;
  circleStyle: SparklineStyles.DataPointCircleStyle;
  labelPosition: 'above' | 'below';
  xOffset?: number; // x-axis offset in pixels
}

/**
 * A miniature line graph of the data points given in the props. The data points will be spread
 * evenly across the x axis, and the y axis will range from 0 to the value of the largest data point
 * (all data values are assumed to be non-negative).
 */
export class Sparkline extends React.Component<SparklineProps, SparklineState> {

  private normalize: Normalizer = new Normalizer();

  private labelElements: { [key: string]: Element | null } = {};
  private svgElement: SVGElement | null = null;

  public constructor(props: SparklineProps) {
    super(props);
    this.state = this.defaultState(props);
  }

  private setStateForFullRender() {
    if (this.state.labeledDataPoints && this.state.labeledDataPoints.length > 0) {
      const labels = this.measureTextAndSetOffsets();
      this.setState({
        labeledDataPoints: labels,
        readyForFullRender: true
      });
    }
  }

  /*
   * Measures each text label and makes sure it will render within the confines of the SVG element.
   */
  private measureTextAndSetOffsets(): DataPointLabelSpec[] {

    if (!this.svgElement) {
      return [];
    }

    const offsetCorrector = new OffsetCorrector(this.svgElement);

    const labels: DataPointLabelSpec[] = [];

    this.state.labeledDataPoints.forEach((label: DataPointLabelSpec) => {
      // expects the labels to have been pushed onto the DOM by the first pass of rendering
      const textElement = this.labelElements[label.key + 'Label'];

      if (!textElement) { return; }

      labels.push({
        ...label, // Shallow copy of `label`
        xOffset: offsetCorrector.correctHorizontalOffset(textElement)
      });
    });

    return labels;
  }

  private defaultState(props: SparklineProps): SparklineState {
    return {
      readyForFullRender: false,
      labeledDataPoints: this.getPointsToLabel(props.dataPoints),
    };
  }

  /*
   * When new props are received, the component must clear its state so that the two-pass render
   * can happen in full again.
   */
  public componentWillReceiveProps(nextProps: SparklineProps) {
    if (!shallowEqual(this.props, nextProps)) {
      this.setState(this.defaultState(nextProps));
    }
  }

  /*
   * As soon as the component has finished the first pass of the two-pass render,
   * perform the measurements and state change that will trigger the second pass.
   */
  public componentDidMount() {
    this.setStateForFullRender();
  }
  public componentDidUpdate() {
    if (!this.state.readyForFullRender) {
      this.setStateForFullRender();
    }
  }

  /*
   * This component renders itself in two passes. The first time, only the text labels are
   * rendered and the element is hidden. Then, on componentDidMount/compenentDidUpdate, the text
   * elements are measured, and the component computes the appropriate layout parameters to make
   * everything fits. Once this new state is set, the render happens again, this time visibly
   * rendering the entire component with the correct spacing.
   */
  public render(): React.ReactElement<React.SVGAttributes<SVGElement>> {
    const fullRender = this.state.readyForFullRender;

    const innerWidth = this.props.width - SparklineStyles.DefaultPadding.paddingLeft
      - SparklineStyles.DefaultPadding.paddingRight;
    const innerHeight = this.props.height - SparklineStyles.DefaultPadding.paddingTop
      - SparklineStyles.DefaultPadding.paddingBottom;
    const subcomponents: React.ReactNode[] = [];

    if (this.props.dataPoints && this.props.dataPoints.length > 0) {
      subcomponents.push(this.svgTitle());

      this.normalize = new Normalizer(this.props.dataPoints, innerWidth, innerHeight,
        SparklineStyles.DefaultPadding.paddingLeft, SparklineStyles.DefaultPadding.paddingTop);
      if (fullRender) {
        subcomponents.push(this.path());
      }

      this.state.labeledDataPoints.forEach((spec: DataPointLabelSpec) => {
        subcomponents.push(this.labelAtDataPoint(spec)); // push to DOM for measureTextAndSetOffsets
        if (fullRender) {
          subcomponents.push(this.circleAtDataPoint(spec));
        }
      });
    }

    return (
      <svg
        ref={this.setSvgElement}
        height={this.props.height}
        width={this.props.width}
        style={SparklineStyles.svgStyle}
        data-tb-test-id='sparkline'
        role='img'
      >
        {...subcomponents}
      </svg>
    );

  }

  private setSvgElement = (element: SVGSVGElement | null) => {
    this.svgElement = element;
  }

  // Creates the path element using the commands from the pathBuilder
  private path(): React.ReactElement<React.SVGAttributes<SVGElement>> {
    const pathBuilder = new PathBuilder(this.normalize);

    return (
      <path
        key='path'
        stroke={SparklineStyles.LineColor}
        strokeWidth={SparklineStyles.LineWidth}
        fill='none'
        d={pathBuilder.buildPath(this.props.dataPoints)}
      />
    );

  }

  private svgTitle(): React.ReactElement<React.SVGAttributes<SVGElement>> {
    return <title>{this.props.title}</title>;
  }

  // Creates the circle element to be placed along the path
  private circleAtDataPoint(spec: DataPointLabelSpec): React.ReactElement<React.SVGAttributes<SVGElement>> {
    const fullName = spec.key + 'Circle';
    return (
      <circle
        key={fullName}
        data-tb-test-id={fullName}
        cx={this.normalize.x(spec.index)}
        cy={this.normalize.y(this.props.dataPoints[spec.index])}
        {...spec.circleStyle}
      />
    );
  }

  /*
   * Returns the label element at data point i, with the data point's value as the circle text.
   * If circleStyle is passed in, it is assumed that a circle exists on that data point with the given style.
   * labelPosition denotes the vertical position of the label with respect to the data point:
   *   'above' for above the point, 'below' for below the point.
   */
  private labelAtDataPoint(spec: DataPointLabelSpec):
    React.ReactElement<React.SVGAttributes<SVGElement>> {
    const labelValue = this.props.dataPoints[spec.index];
    const fullName = spec.key + 'Label';

    const textBuffer = spec.circleStyle.r + spec.circleStyle.strokeWidth + SparklineStyles.TextBuffer;
    const yOffset = (spec.labelPosition === 'above') ? -textBuffer : textBuffer + SparklineStyles.LabelStyle.fontSize - 1.5;

    return (
      <text
        ref={this.assignTextRef(fullName)}
        key={fullName}
        data-tb-test-id={fullName}
        style={this.state.readyForFullRender
          ? { ...SparklineStyles.LabelStyle, visibility: 'visible' }
          : { ...SparklineStyles.LabelStyle, visibility: 'hidden' }}
        x={this.normalize.x(spec.index)}
        y={this.normalize.y(labelValue)}
        dx={spec.xOffset}
        dy={yOffset}
        textAnchor={'middle'}
      >
        {labelValue}
      </text>
    );
  }

  private assignTextRef = (fullName: string) => (element: SVGTextElement) => {
    this.labelElements[fullName] = element;
  }

  // puts labels on the endpoint, the max, and the min
  private getPointsToLabel(dataPoints: ReadonlyArray<number> | null): DataPointLabelSpec[] {

    const labelPoints: DataPointLabelSpec[] = [];

    if (!dataPoints || dataPoints.length === 0) {
      return labelPoints;
    }

    const minIndex = dataPoints.indexOf(Math.min(...dataPoints));
    const maxIndex = dataPoints.indexOf(Math.max(...dataPoints));
    const endIndex = dataPoints.length - 1;
    const endPointHigher: boolean = dataPoints[endIndex] >= dataPoints[endIndex - 1];


    // pushing the circle styling onto the endpoint
    labelPoints.push({
      key: 'end',
      index: endIndex,
      circleStyle: SparklineStyles.CircleStyleEnd,
      labelPosition: (endPointHigher ? 'above' : 'below')
    });

    // pushing the circle styling onto the maxpoint (assuming it's not the endpoint)
    if (endIndex !== maxIndex) {
      labelPoints.push({
        key: 'max',
        index: maxIndex,
        circleStyle: SparklineStyles.CircleStyleMax,
        labelPosition: 'above'
      });
    }

    // pushing the circle styling onto the minpoint (assuming it's not the end or the max)
    if (endIndex !== minIndex && maxIndex !== minIndex) {
      labelPoints.push({
        key: 'min',
        index: minIndex,
        circleStyle: SparklineStyles.CircleStyleMin,
        labelPosition: 'below'
      });
    }

    return labelPoints;
  }
}
